// Ticket Status Enum
export enum TicketStatus {
    Active = 0,
    Used = 1,
    Expired = 2,
    Canceled = 3
}

// Ticket Type Enum
export enum TicketTypeEnum {
    OneTime = 1,
    Monthly = 2
}

// Transaction Action Type Enum
export enum TransactionActionType {
    Sell = 1,
    Use = 2,
    Cancel = 3,
    Refund = 4,
    Extend = 5,
    Giveaway = 6
}

// Ticket Type Model
export interface ITicketType {
    id?: string;
    code: string;
    name: string;
    price: number;
    defaultExpireDays: number;
    description?: string;
    type: TicketTypeEnum;
    isActive: boolean;
    buyQuantity?: number;
    giftQuantity?: number;
    applicableRoles?: number[];
}

// Ticket Model
export interface ITicket {
    id: string;
    code: string;
    studentId: string;
    studentName: string;
    studentUserInternalId: string;
    createdBy: string;
    createdByName: string;
    ticketTypeId: string;
    ticketTypeName: string;
    ticketTypeCode: string;
    price: number;
    expireDate: string;
    status: TicketStatus;
    note?: string;
    qrCode?: string;
    profilePicUrl?: string;
    creationDate?: string;
    type?: number; // Loại phiếu: 1 = OneTime, 2 = Monthly
    transactions?: ITicketTransaction[];
}

// Ticket Transaction Model
export interface ITicketTransaction {
    id: string;
    ticketId: string;
    transactionType: TransactionActionType;
    detail: string;
    note?: string;
    actionDate: string;
    actionBy: string;
    actionByName?: string;
}

// Create Ticket Request
export interface ICreateTicketRequest {
    studentId: string;
    ticketTypeId: string;
    note?: string;
}

// Use Ticket Request
export interface IUseTicketRequest {
    ticketId: string;
    note?: string;
}

// Cancel Ticket Request
export interface ICancelTicketRequest {
    ticketId: string;
    note: string;
}

// Paging Request
export interface ITicketPagingRequest {
    pageIndex: number;
    pageSize: number;
    freeTextSearch?: string;
    studentId?: string;
    ticketTypeId?: string;
    status?: TicketStatus;
    fromDate?: string;
    toDate?: string;
}

// Revenue Statistics Request
export interface IRevenueStatisticsRequest {
    startDate: string;
    endDate: string;
    userId?: string;
}

// Revenue Statistics Response
export interface IRevenueStatistics {
    totalTicketsSold: number;
    totalTicketsUsed: number;
    totalRevenue: number;
    ticketTypeStatistics: ITicketTypeStatistics[];
}

export interface ITicketTypeStatistics {
    ticketTypeId: string;
    ticketTypeCode: string;
    ticketTypeName: string;
    ticketsSold: number;
    ticketsUsed: number;
    revenue: number;
}

// Helper functions
export const getTicketStatusText = (status: TicketStatus): string => {
    switch (status) {
        case TicketStatus.Active:
            return 'Đang hoạt động';
        case TicketStatus.Used:
            return 'Đã sử dụng';
        case TicketStatus.Expired:
            return 'Hết hạn';
        case TicketStatus.Canceled:
            return 'Đã hủy';
        default:
            return 'Không xác định';
    }
};

export const getTicketStatusColor = (status: TicketStatus): string => {
    switch (status) {
        case TicketStatus.Active:
            return 'success';
        case TicketStatus.Used:
            return 'default';
        case TicketStatus.Expired:
            return 'warning';
        case TicketStatus.Canceled:
            return 'error';
        default:
            return 'default';
    }
};

export const getTicketTypeText = (type: TicketTypeEnum): string => {
    switch (type) {
        case TicketTypeEnum.OneTime:
            return 'Vé 1 lần';
        case TicketTypeEnum.Monthly:
            return 'Vé tháng';
        default:
            return 'Không xác định';
    }
};

export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
};

export const getTransactionActionText = (actionType: TransactionActionType): string => {
    switch (actionType) {
        case TransactionActionType.Sell:
            return 'Bán phiếu';
        case TransactionActionType.Use:
            return 'Sử dụng phiếu';
        case TransactionActionType.Cancel:
            return 'Hủy phiếu';
        case TransactionActionType.Refund:
            return 'Hoàn tiền';
        case TransactionActionType.Extend:
            return 'Gia hạn phiếu';
        case TransactionActionType.Giveaway:
            return 'Tặng phiếu';
        default:
            return 'Không xác định';
    }
};

export const getTransactionActionColor = (actionType: TransactionActionType): string => {
    switch (actionType) {
        case TransactionActionType.Sell:
            return 'green';
        case TransactionActionType.Use:
            return 'blue';
        case TransactionActionType.Cancel:
            return 'red';
        case TransactionActionType.Refund:
            return 'orange';
        case TransactionActionType.Extend:
            return 'purple';
        case TransactionActionType.Giveaway:
            return 'gold';
        default:
            return 'default';
    }
};
